import React from "react";
import { useForm } from "react-hook-form";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faKey } from "@fortawesome/free-solid-svg-icons";
import Toster from "../utils/Toaster";
import { handleAdminResetPassword } from "../services/APIClient";
import { STATUS_CODES } from "../utils/statusCode";
import { useLoader } from '../contexts/LoaderContext';

const ResetPasswordModel = ({ isOpen, onClose, getEmail }) => {

    const {
        register,
        handleSubmit,
        getValues,
        setValue,
        formState: { errors },
    } = useForm();
    const { setLoading } = useLoader();

    const onSubmit = async (formdata) => {
        const postData = new FormData();
        postData.append("email", getEmail);
        const code = [formdata.otp1, formdata.otp2, formdata.otp3, formdata.otp4].join("");
        postData.append("code", code);
        postData.append("password", formdata.newPassword);
        postData.append("confirmPassword", formdata.confirmPassword);

        try {
            setLoading(true);
            const response = await handleAdminResetPassword(postData);
            if (response?.code == STATUS_CODES.SUCCESS) {
                Toster("Password changed successfully", "success");
                onClose();
            }
            setLoading(false);
        } catch (error) {
            setLoading(false);
        }

    };

    const handleClose = () => {
        setValue('otp1', '')
        setValue('otp2', '')
        setValue('otp3', '')
        setValue('otp4', '')
        setValue('newPassword', '')
        setValue('confirmPassword', '')
        onClose();
    }

    return (
        <>
            {isOpen && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-gray-800 bg-opacity-50">
                    <div className="bg-white w-full max-w-[550px] m-10 p-6 rounded-lg shadow-lg">
                        <div className="flex justify-center mb-4">
                            <div className="bg-gray-100 p-3 rounded-full">
                                <FontAwesomeIcon
                                    icon={faKey}
                                    className="text-gray-800 text-4xl"
                                />
                            </div>
                        </div>

                        <h2 className="text-center text-2xl font-semibold text-gray-800 mb-4">
                            Reset Password
                        </h2>

                        {/* Form for Resetting Password */}
                        <form onSubmit={handleSubmit(onSubmit)}>
                            {/* OTP Input Group */}
                            <div className="mb-6">
                                <label className="block text-sm text-center font-medium text-gray-700 mb-2">
                                    Enter OTP
                                </label>
                                <div style={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
                                    {Array(4)
                                        .fill("")
                                        .map((_, index) => (
                                            <input
                                                key={index}
                                                autoComplete="off"
                                                aria-label={`Please enter OTP character ${index + 1}`}
                                                type="text"
                                                maxLength="1"
                                                {...register(`otp${index + 1}`, {
                                                    required: "All OTP fields are required.",
                                                    pattern: {
                                                        value: /^[0-9]$/,
                                                        message: "Only numbers are allowed.",
                                                    },
                                                })}
                                                onInput={(e) => {
                                                    e.target.value = e.target.value.replace(/[^0-9]/g, ""); // Ensure only numbers are entered
                                                }}
                                                onChange={(e) => {
                                                    if (e.target.value.length === 1) {
                                                        const nextInput = document.querySelector(`input[aria-label='Please enter OTP character ${index + 2}']`);
                                                        if (nextInput) nextInput.focus();
                                                    }
                                                }}
                                                onKeyDown={(e) => {
                                                    if (e.key === "Backspace" && !e.target.value) {
                                                        const prevInput = document.querySelector(`input[aria-label='Please enter OTP character ${index}']`);
                                                        if (prevInput) prevInput.focus();
                                                    }
                                                }}
                                                className={`w-10 h-10 text-center border ${errors[`otp${index + 1}`]
                                                    ? "border-red-500"
                                                    : "border-gray-300"
                                                    } rounded-md mx-1 focus:outline-none focus:ring-2 ${errors[`otp${index + 1}`]
                                                        ? "focus:ring-red-500"
                                                        : "focus:ring-blue-500"
                                                    }`}
                                            />
                                        ))}
                                </div>
                                {Object.keys(errors).some((key) => key.startsWith("otp")) && (
                                    <p className="text-center text-red-500 text-sm mt-1">
                                        Please fill in all OTP fields.
                                    </p>
                                )}
                            </div>

                            <div className="mb-4">
                                <label
                                    htmlFor="newPassword"
                                    className="block text-sm font-medium text-gray-700 mb-1"
                                >
                                    New Password
                                </label>
                                <input
                                    type="password"
                                    id="newPassword"
                                    {...register("newPassword", {
                                        required: "New password is required.",
                                        pattern: {
                                            value: /^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[#$%^&*@~!]).{8,20}$/,
                                            message: "Entered password must contain one uppercase, lowercase, number, and a special character.",
                                        },
                                        minLength: {
                                            value: 8,
                                            message: "Password must be at least 8 characters long.",
                                        },
                                        maxLength: {
                                            value: 20,
                                            message: 'Password cannot exceed 20 characters.',
                                        },
                                    })}
                                    className={`w-full px-4 py-2 border ${errors.newPassword
                                        ? "border-red-500"
                                        : "border-gray-300"
                                        } rounded-md focus:outline-none focus:ring-2 ${errors.newPassword
                                            ? "focus:ring-red-500"
                                            : "focus:ring-blue-500"
                                        }`}
                                    placeholder="Enter new password"
                                />
                                {errors.newPassword && (
                                    <p className="text-red-500 text-sm mt-1">
                                        {errors.newPassword.message}
                                    </p>
                                )}
                            </div>
                            <div className="mb-6">
                                <label
                                    htmlFor="confirmPassword"
                                    className="block text-sm font-medium text-gray-700 mb-1"
                                >
                                    Confirm Password
                                </label>
                                <input
                                    type="password"
                                    id="confirmPassword"
                                    {...register("confirmPassword", {
                                        required: "Confirm password is required.",
                                        validate: (value) =>
                                            value === getValues("newPassword") ||
                                            "Password does not match.",
                                    })}
                                    className={`w-full px-4 py-2 border ${errors.confirmPassword
                                        ? "border-red-500"
                                        : "border-gray-300"
                                        } rounded-md focus:outline-none focus:ring-2 ${errors.confirmPassword
                                            ? "focus:ring-red-500"
                                            : "focus:ring-blue-500"
                                        }`}
                                    placeholder="Confirm new password"
                                />
                                {errors.confirmPassword && (
                                    <p className="text-red-500 text-sm mt-1">
                                        {errors.confirmPassword.message}
                                    </p>
                                )}
                            </div>

                            {/* Action Buttons */}
                            <div className="flex justify-between">
                                <button
                                    type="button"
                                    onClick={() => handleClose()}
                                    className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    className="px-4 py-2 bg-gray-800 text-white rounded-md hover:bg-gray-900"
                                >
                                    Submit
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </>
    );
};

export default ResetPasswordModel;
